import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Shell } from '../components/Shell';
import { ClassicHome, WhatsAppFab } from '../components/themes/Classic';
import { BoldHome } from '../components/themes/Bold';
import { MinimalHome } from '../components/themes/Minimal';
import { MarketingLanding } from '../components/MarketingLanding';
import type { HomeProps } from '../components/themes/types';
import { useCopy, useSettings } from '../lib/settings';
import { isMarketingSite } from '../lib/hostname';
import { uploadStore } from '../lib/uploadStore';

export const Route = createFileRoute('/')({
  component: HomePage,
});

function HomePage() {
  if (isMarketingSite()) return <MarketingLanding />;
  return <TenantHomePage />;
}

function TenantHomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = useSettings();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadStore.set(file);
    void navigate({ to: '/confirm' });
  }

  const props: HomeProps = {
    headline: useCopy('copy.heroHeadline'),
    subheadline: useCopy('copy.heroSubheadline'),
    instantBadge: useCopy('copy.instantBadge'),
    pillars: [
      { label: useCopy('copy.pillar1'), icon: settings['pillars.icon1'] },
      { label: useCopy('copy.pillar2'), icon: settings['pillars.icon2'] },
      { label: useCopy('copy.pillar3'), icon: settings['pillars.icon3'] },
      { label: useCopy('copy.pillar4'), icon: settings['pillars.icon4'] },
    ],
    brandName: settings['brand.name'],
    onCamera: () => cameraRef.current?.click(),
    onGallery: () => galleryRef.current?.click(),
    ctaLabel: t('landing.cta'),
    ctaUploadLabel: t('landing.cta_upload'),
    steps: [t('landing.step_1'), t('landing.step_2'), t('landing.step_3')],
    stepsTitle: t('landing.how_it_works'),
    websiteUrl: settings['links.websiteUrl'],
    websiteLabel: settings['links.websiteLabel'],
    showWhatsapp: settings['links.showWhatsappOnHome'],
    waNumber: settings['contact.whatsappNumber'],
    waButtonLabel: 'تواصل عبر واتساب',
    heroVideoUrl: settings['hero.videoUrl'] || undefined,
    heroVideoEnabled: settings['hero.videoEnabled'] ?? false,
  };

  const theme = settings['theme.homepage'];
  const ThemeComponent =
    theme === 'bold' ? BoldHome : theme === 'minimal' ? MinimalHome : ClassicHome;

  return (
    <Shell showFooter heroVideo={!!props.heroVideoEnabled && !!props.heroVideoUrl}>
      <ThemeComponent {...props} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      {props.showWhatsapp && props.waNumber ? <WhatsAppFab waNumber={props.waNumber} /> : null}
    </Shell>
  );
}
